import { useEffect, useState, useRef, useCallback } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { AuthAPI } from "../api";
import RequestSessionModal from "../components/RequestSessionModal";
import StudentCounterModal from "../components/StudentCounterModal";
import { useParams, useSearchParams } from "react-router-dom";
import MentorRequestModal from "../components/MentorRequestModal";
import { useAuth } from "../context/AuthContext";

function CalendarBooking() {
  const { isAuthenticated, isValidating, user } = useAuth();
  const [events, setEvents] = useState([]);

  // Shared request modal state (used for both date-clicks and available slot clicks)
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDateInfo, setSelectedDateInfo] = useState(null);
  const [modalMentorId, setModalMentorId] = useState(null);

  const [counterModalOpen, setCounterModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [mentorModalOpen, setMentorModalOpen] = useState(false);

  // Mentor list for the date-click picker (students only)
  const [mentors, setMentors] = useState([]);
  const [modalMentorsForPicker, setModalMentorsForPicker] = useState(null);
  const [modalLockedMentor, setModalLockedMentor] = useState(null);

  const [toast, setToast] = useState(null);
  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const { mentorId } = useParams();
  const [searchParams] = useSearchParams();
  const focusRequestId = searchParams.get("request_id");
  const focusSessionId = searchParams.get("session_id");
  const calendarRef = useRef(null);
  const isMentor = user?.is_mentor === true;

  const [eventFilters, setEventFilters] = useState({
    available: true,
    confirmed: true,
    request: true,
    cancelled: false,
  });

  const filteredEvents = events.filter(
    (event) => eventFilters[event.extendedProps?.type]
  );

  const EVENT_TYPES = {
    available: { label: "Available slots", color: "bg-green-500" },
    confirmed: { label: "Confirmed sessions", color: "bg-[#282E6A]" },
    request: { label: "Requested sessions", color: "bg-amber-500" },
    cancelled: { label: "Cancelled / Rejected", color: "bg-red-400" },
  };

  const loadCalendarData = useCallback(async () => {
    try {
      const availabilityUrl = mentorId ? `availability/?mentor=${mentorId}` : "availability/";
      const sessionsUrl = mentorId ? `sessions/?mentor=${mentorId}` : "sessions/";
      const requestsUrl = mentorId ? `session-requests/?mentor=${mentorId}` : "session-requests/";

      const [availabilityRes, sessionsRes, requestsRes] = await Promise.all([
        AuthAPI.get(availabilityUrl),
        AuthAPI.get(sessionsUrl),
        AuthAPI.get(requestsUrl),
      ]);

      const availabilityEvents = availabilityRes.data
        .filter((slot) => !slot.is_booked)
        .map((slot) => ({
          id: `avail-${slot.id}`,
          title: `Available – ${slot.mentor.profile.user.username}`,
          start: slot.start_time,
          end: slot.end_time,
          display: "background",
          backgroundColor: "rgba(34, 197, 94, 0.89)",
          extendedProps: { type: "available", slotId: slot },
        }));

      const sessionEvents = sessionsRes.data.map((session) => {
        const cancelled = session.status === "cancelled";
        return {
          id: `session-${session.id}`,
          title: `with ${session.mentor}`,
          start: session.start_time,
          end: session.end_time,
          backgroundColor: cancelled ? "#F87171" : "#282E6A",
          borderColor: cancelled ? "#EF4444" : "#1e2458",
          extendedProps: { type: cancelled ? "cancelled" : "confirmed", sessionId: session },
        };
      });

      const requestEvents = requestsRes.data
        .filter((req) => req.status !== "approved")
        .map((req) => {
          const rejected = req.status === "rejected";
          return {
            id: `req-${req.id}`,
            title: `Request (${req.status}) – ${req.mentor}`,
            start: req.requested_start_time,
            end: req.requested_end_time,
            backgroundColor: rejected ? "#F87171" : "#F59E0B",
            borderColor: rejected ? "#EF4444" : "#D97706",
            extendedProps: { type: rejected ? "cancelled" : "request", status: req.status, requestId: req },
          };
        });

      setEvents([...availabilityEvents, ...sessionEvents, ...requestEvents]);

      setTimeout(() => {
        if (!calendarRef.current) return;
        const api = calendarRef.current.getApi();
        let target = null;

        if (focusRequestId) {
          target = api.getEvents().find(
            (e) => e.extendedProps?.requestId?.id === Number(focusRequestId)
          );
        }
        if (focusSessionId) {
          target = api.getEvents().find(
            (e) => e.extendedProps?.sessionId?.id === Number(focusSessionId)
          );
        }
        if (target) {
          api.gotoDate(target.start);
          const hours = target.start.getHours().toString().padStart(2, "0");
          api.scrollToTime(`${hours}:00:00`);
        }
      }, 300);
    } catch (err) {
      console.error("Calendar load error:", err);
    }
  }, [mentorId, focusRequestId, focusSessionId]);

  useEffect(() => {
    if (isValidating || !isAuthenticated) return;
    setEventFilters((prev) => ({ ...prev, available: mentorId ? true : !isMentor }));
    loadCalendarData();
  }, [isAuthenticated, isMentor, isValidating, mentorId, loadCalendarData]);

  // Fetch mentor list once for the date-click picker (students only)
  useEffect(() => {
    if (isValidating || !isAuthenticated) return;
    if (!isMentor) {
      AuthAPI.get("mentors/").then((res) => setMentors(res.data)).catch(console.error);
    }
  }, [isAuthenticated, isValidating, isMentor]);

  const openRequestModal = (dateInfo, resolvedMentorId, mentorsForPicker = null, lockedMentor = null) => {
    setSelectedDateInfo(dateInfo);
    setModalMentorId(resolvedMentorId);
    setModalMentorsForPicker(mentorsForPicker);
    setModalLockedMentor(lockedMentor);
    setModalOpen(true);
  };

  const closeRequestModal = () => {
    setModalOpen(false);
    setSelectedDateInfo(null);
    setModalMentorId(null);
    setModalMentorsForPicker(null);
    setModalLockedMentor(null);
    loadCalendarData();
  };

  const handleEventClick = async (info) => {
    const { type, status } = info.event.extendedProps;

    if (isMentor) {
      if (type === "request" && status === "pending") {
        setSelectedRequest(info.event.extendedProps?.requestId);
        setMentorModalOpen(true);
      }
      return;
    }

    if (type === "request" && status === "countered") {
      setSelectedRequest(info.event.extendedProps?.requestId);
      setCounterModalOpen(true);
    }
  };

  const handleMentorAction = async (action, payload) => {
    try {
      if (action === "approve") await AuthAPI.patch(`session-requests/${selectedRequest.id}/approve/`);
      if (action === "reject") await AuthAPI.patch(`session-requests/${selectedRequest.id}/reject/`, payload);
      if (action === "counter") await AuthAPI.patch(`session-requests/${selectedRequest.id}/counter/`, payload);
      setMentorModalOpen(false);
      loadCalendarData();
    } catch (err) {
      console.error("Mentor action failed", err);
      showToast("Action failed. Please try again.", "error");
    }
  };

  // Blank date clicks (and clicks on background available-slot regions)
  const handleDateClick = (info) => {
    if (isMentor) return;
    const clickedTime = new Date(info.date);
    // If click lands inside an available slot, pre-select that slot's mentor
    const matchingSlot = events.find((e) => {
      if (e.extendedProps?.type !== "available") return false;
      const slot = e.extendedProps.slotId;
      return clickedTime >= new Date(slot.start_time) && clickedTime < new Date(slot.end_time);
    });
    const preSelectedId = matchingSlot
      ? matchingSlot.extendedProps.slotId.mentor.id
      : (mentorId || null);
    openRequestModal({ date: clickedTime }, preSelectedId, mentors, null);
  };

  const handleAcceptCounter = async () => {
    try {
      await AuthAPI.patch(`session-requests/${selectedRequest.id}/approve/`);
      setCounterModalOpen(false);
      loadCalendarData();
    } catch (err) {
      console.error("Accept failed", err);
      showToast("Booking failed. Please check you have enough credits.", "error");
    }
  };

  const handleRejectCounter = async () => {
    try {
      await AuthAPI.patch(`session-requests/${selectedRequest.id}/reject/`, {
        mentor_message: "Student rejected proposed time",
      });
      setCounterModalOpen(false);
      loadCalendarData();
    } catch (err) {
      console.error("Reject failed", err);
      showToast("Failed to reject. Please try again.", "error");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">

      {/* Toast */}
      {toast && (
        <div className={`mb-4 flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium ${
          toast.type === "success"
            ? "bg-green-50 text-green-800 border border-green-200"
            : "bg-red-50 text-red-800 border border-red-200"
        }`}>
          {toast.type === "success" ? (
            <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          {toast.message}
        </div>
      )}

      {/* Header + legend */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Calendar</h1>
          <p className="mt-0.5 text-sm text-gray-600">
            {mentorId
              ? "Click an available slot or any date to request a session."
              : "View and manage your scheduled sessions."}
          </p>
        </div>

        {/* Legend / filters */}
        <div className="flex flex-wrap items-center gap-4">
          {Object.entries(EVENT_TYPES).map(([type, cfg]) => (
            <label key={type} className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={eventFilters[type]}
                onChange={() => setEventFilters((prev) => ({ ...prev, [type]: !prev[type] }))}
                className="h-4 w-4 rounded border-gray-300 text-[#282E6A] focus:ring-[#282E6A]"
              />
              <span className={`w-3 h-3 rounded-full flex-shrink-0 ${eventFilters[type] ? cfg.color : "bg-gray-200"}`} />
              <span className={`text-sm ${eventFilters[type] ? "text-gray-800" : "text-gray-400"}`}>
                {cfg.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      <FullCalendar
        timeZone="local"
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="timeGridWeek"
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay",
        }}
        events={filteredEvents}
        eventClick={handleEventClick}
        dateClick={handleDateClick}
        selectable={true}
        height="calc(100vh - 260px)"
        expandRows={true}
        scrollTimeReset={false}
        eventMinHeight={40}
        slotMinTime="00:00:00"
        slotMaxTime="24:00:00"
        ref={calendarRef}

        eventContent={(arg) => {
          // Background events render their label via eventDidMount
          if (arg.event.extendedProps?.type === "available") return null;

          const { type, status, sessionId, requestId } = arg.event.extendedProps;

          let label;
          if (type === "confirmed") label = "Session";
          else if (type === "cancelled" && sessionId) label = "Cancelled";
          else if (status === "pending") label = "Pending request";
          else if (status === "countered") label = "Counter offer";
          else if (type === "cancelled" && requestId) label = "Rejected";
          else label = type;

          // Use computed full name for both sessions and requests
          const mentorName =
            requestId?.mentor_name || sessionId?.mentor_name ||
            requestId?.mentor || sessionId?.mentor || "";

          const studentName =
            requestId?.student_name || sessionId?.student_name ||
            requestId?.student || sessionId?.student || "";

          const printName = isMentor ? studentName : mentorName;

          return (
            <div className="flex flex-col px-1.5 py-0.5 text-white gap-0.5">
              <div className="text-xs font-semibold leading-tight">{printName}</div>
              <div className="text-xs font-medium leading-tight opacity-90">{label}</div>
            </div>
          );
        }}

        eventDidMount={(info) => {
          info.el.title = info.event.title;
          // Background available-slot events: override FC's default opacity:0.3, inject label
          if (info.event.extendedProps?.type === "available") {
            info.el.style.opacity = "1";
            const slot = info.event.extendedProps.slotId;
            const u = slot.mentor.profile.user;
            const mentorName = `${u.first_name} ${u.last_name}`.trim() || u.username;
            const existing = info.el.querySelector("[data-slot-label='true']");
            if (existing) existing.remove();

            const label = document.createElement("div");
            label.setAttribute("data-slot-label", "true");
            label.style.cssText =
              "position:absolute;inset:0;padding:4px 7px;font-size:11px;pointer-events:none;" +
              "overflow:hidden;line-height:1.5;color:rgba(0,0,0,0.85);";

            const nameLine = document.createElement("span");
            nameLine.style.fontWeight = "700";
            nameLine.textContent = mentorName;

            const typeLine = document.createElement("span");
            typeLine.style.display = "block";
            typeLine.style.fontWeight = "500";
            typeLine.style.opacity = "0.8";
            typeLine.textContent = "Available slot";

            label.appendChild(nameLine);
            label.appendChild(typeLine);
            info.el.appendChild(label);
          }
          if (
            (focusRequestId && Number(info.event.extendedProps?.requestId?.id) === Number(focusRequestId)) ||
            (focusSessionId && Number(info.event.extendedProps?.sessionId?.id) === Number(focusSessionId))
          ) {
            info.el.style.outline = "3px solid #fbbf24";
            info.el.style.outlineOffset = "1px";
            info.el.style.zIndex = 10;
          }
        }}
      />

      <RequestSessionModal
        isOpen={modalOpen}
        dateInfo={selectedDateInfo}
        mentorId={modalMentorId}
        mentors={modalMentorsForPicker}
        lockedMentor={modalLockedMentor}
        onClose={closeRequestModal}
      />

      <StudentCounterModal
        isOpen={counterModalOpen}
        request={selectedRequest}
        onClose={() => setCounterModalOpen(false)}
        onAccept={handleAcceptCounter}
        onReject={handleRejectCounter}
      />

      <MentorRequestModal
        isOpen={mentorModalOpen}
        request={selectedRequest}
        onClose={() => setMentorModalOpen(false)}
        onAction={handleMentorAction}
      />
    </div>
  );
}

export default CalendarBooking;
